import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const DISPLAY_LATEX_ENV_RE = /\\begin\{(aligned|align|equation|gather|split|cases|matrix|pmatrix|bmatrix|vmatrix)\}/;

const renderKatexSpan = (latex, displayMode, key) => {
  try {
    const html = katex.renderToString(latex, { displayMode, throwOnError: false });
    return (
      <span
        key={key}
        className="katex-inline-wrapper"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch (err) {
    console.error('KaTeX error:', err);
    return <code key={key}>{latex}</code>;
  }
};

const mapAttributes = (attributes) => {
  const attrs = {};
  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes[i];
    if (attr.name === 'class') {
      attrs.className = attr.value;
    } else if (attr.name === 'style') {
      const styleObj = {};
      attr.value.split(';').forEach((styleStr) => {
        const parts = styleStr.split(':');
        if (parts.length >= 2) {
          const rule = parts[0].trim();
          const val = parts.slice(1).join(':').trim();
          if (rule && val) {
            // camelCase the CSS property rule
            const camelRule = rule.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
            styleObj[camelRule] = val;
          }
        }
      });
      attrs.style = styleObj;
    } else {
      attrs[attr.name] = attr.value;
    }
  }
  return attrs;
};

const domToReact = (node, index = 0) => {
  if (node.nodeType === 3) {
    const text = node.textContent;
    if (!text) return null;

    // Split by display math $$...$$ first, then inline math $...$
    const regex = /(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/g;
    const parts = text.split(regex);

    if (parts.length === 1) {
      return text;
    }

    return parts.map((part, pIdx) => {
      const key = `${index}-${pIdx}`;
      if (part.startsWith('$$') && part.endsWith('$$')) {
        return renderKatexSpan(part.slice(2, -2), true, key);
      } else if (part.startsWith('$') && part.endsWith('$')) {
        return renderKatexSpan(part.slice(1, -1), false, key);
      } else {
        return part;
      }
    });
  }

  if (node.nodeType === 1) {
    const tagName = node.tagName.toLowerCase();

    if (tagName === 'span') {
      const dataType = node.getAttribute('data-type');
      const latex = node.getAttribute('latex');
      if (latex && (dataType === 'inline-latex' || dataType === 'block-latex')) {
        const displayMode = dataType === 'block-latex' || DISPLAY_LATEX_ENV_RE.test(latex);
        return renderKatexSpan(latex, displayMode, index);
      }
    }

    const attrs = mapAttributes(node.attributes);
    attrs.key = index;

    const children = Array.from(node.childNodes).map((child, cIdx) => domToReact(child, cIdx));
    const elementChildren = children.length > 0 ? children : null;

    return React.createElement(tagName, attrs, elementChildren);
  }

  return null;
};

const isImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return /^https?:\/\/.+\.(png|jpg|jpeg|webp|gif|svg)(\?.*)?$/i.test(trimmed);
};

export default function LatexRenderer({ text }) {
  if (text === null || text === undefined) return null;
  const textStr = String(text);
  if (!textStr) return null;

  if (isImageUrl(textStr)) {
    return (
      <img
        src={textStr}
        alt="Question Content"
        style={{
          maxWidth: '90%',
          maxHeight: '265px',
          borderRadius: '12px',
          border: '1.5px solid #cbd5e1',
          objectFit: 'contain',
          display: 'block',
          margin: '12px auto'
        }}
      />
    );
  }

  const parser = new DOMParser();

  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(textStr);
  const processedText = hasHtmlTags ? textStr : textStr.replace(/\n/g, '<br />');

  const doc = parser.parseFromString(`<div>${processedText}</div>`, 'text/html');
  const rootNode = doc.body.firstChild;

  if (!rootNode) return null;

  const children = Array.from(rootNode.childNodes).map((child, idx) => domToReact(child, idx));
  return <>{children}</>;
}
